import React, { useEffect, useState } from 'react';
import { DetailBox, PaginatedTable, TitleBar, SearchBar, DropDown } from '../../../common';
import {
  TITLES,
  ROUTES,
  FABRIC_PRODUCTION_DETAILS_COLUMN,
  BUTTON,
  LOCAL_STORAGE,
  POPUP_TYPE,
} from '../../../../constants';
import { useLocation } from "react-router-dom";
import { useNavigate } from "../../../../backend/navigation";
import { FabricProductionModal } from './FabricProduction';
import { Button, PopUpBox, ThresholdWarningPopup } from '../../../shared';
import { dispatchDetailsBox } from '../../../../utils/dataCompiler/dispatchDataCompiler';
import { useDebounce } from '../../../../hooks';
import { vendorReceipt, vendorReceiptMoveIds } from '../../../../service/receipt';
import { validateBulkManufacturing, saveAsDraftManufacturing, vendorUpdateDispatchDetail } from '../../../../service';
import { vendorModalDispatch } from '../../../../service'
import { fabricManufacturingProductCompiler, getLocalStorageItem, popup } from '../../../../utils';

export const ProductionDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productData, setProductData] = useState([]);
  const [popUpData, setPopUpData] = useState({});
  const [reload, setReload] = useState(false);
  const [column, setColumn] = useState(FABRIC_PRODUCTION_DETAILS_COLUMN);
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [fullProductData, setFullProductData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoveIdsLoading, setIsMoveIdsLoading] = useState(false);
  const [showCollapseTable, setShowCollapseTable] = useState(false);
  const [isValidateLoading, setIsValidateLoading] = useState(false);
  const [editValues, setEditValues] = useState({
    invoice_no: state?.invoice_no || '',
  });
  const [isUpdateDetailsLoading, setIsUpdateDetailsLoading] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [showThresholdWarning, setShowThresholdWarning] = useState(false);
  const [thresholdWarningData, setThresholdWarningData] = useState(null);
  const [pendingSaveCallback, setPendingSaveCallback] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState(false);
  const [readyStatusFilter, setReadyStatusFilter] = useState('All');
  const [readyStatusOptions, setReadyStatusOptions] = useState(['All', 'Not Ready', 'Partially Ready', 'Fully Ready']);

  useEffect(() => {
    setEditValues(() => ({
      invoice_no: invoiceNo,
    }));
  }, [invoiceNo]);

  // Update productData when fullProductData changes (server-side filtering)
  useEffect(() => {
    setProductData(fullProductData);
  }, [fullProductData]);

  useEffect(() => {
    const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
    setShowCollapseTable(vendorType === 'fabric' && !['done', 'cancel'].includes(state?.state));
  }, [state?.state]);


  let enableValidate = () => {
    if (getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing') {
      return productData.length
        ? productData.some(move => move.lotno && move.draft_produced_qty > 0)
        : false;
    } else {
      return productData.length
        ? productData
          .filter(move => move.force_consumption === false)
          .every(move => move.lotno && move.draft_produced_qty > 0)
        : false;
    }
  };

  const handleSaveAsDraft = async (picking_ids = [], container_number = false, force_submit = false, move_ids = []) => {
    try {
      const response = await saveAsDraftManufacturing({ 
        data: { 
          move_ids, 
          picking_ids, 
          container_number,
          force_submit 
        } 
      });
      
      // Check if response is a warning
      if (response && response.status === 'warning' && (response.warning_type === 'threshold_exceeded' || response.warning_type === 'damage_threshold_exceeded')) {
        // Return warning object instead of true/false
        return { warning: true, data: response };
      }
      
      if (response) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error saving as draft:', error);
      return false;
    }
  }

  const handleThresholdWarningConfirm = async (editedValues = []) => {
    setShowThresholdWarning(false);
    
    if (pendingSaveCallback) {
      // Update move_ids with edited quantities if provided
      let move_ids = [];
      if (editedValues && editedValues.length > 0 && pendingSaveCallback.move_ids) {
        // Update existing move_ids with edited quantities
        move_ids = pendingSaveCallback.move_ids.map(move => {
          // Convert both to numbers for comparison
          const moveIdNum = parseInt(move.move_id);
          const edited = editedValues.find(ev => {
            const evMoveIdNum = parseInt(ev.moveId);
            return evMoveIdNum === moveIdNum;
          });
          if (edited) {
            return {
              ...move,
              producing_quantity: parseFloat(edited.quantity) || 0
            };
          }
          return move;
        });
      } else {
        move_ids = pendingSaveCallback.move_ids || [];
      }
      
      // Re-call save_as_draft with force_submit=true and updated move_ids
      const { picking_ids, container_number } = pendingSaveCallback;
      const result = await handleSaveAsDraft(picking_ids, container_number, true, move_ids);
      
      // Execute the callback with the result
      if (pendingSaveCallback.onComplete) {
        pendingSaveCallback.onComplete(result === true);
      }
      
      setPendingSaveCallback(null);
    }
  }

  const handleThresholdWarningCancel = () => {
    setShowThresholdWarning(false);
    setThresholdWarningData(null);
    
    if (pendingSaveCallback && pendingSaveCallback.onCancel) {
      pendingSaveCallback.onCancel();
    }
    
    setPendingSaveCallback(null);
    setIsValidateLoading(false);
  }

  const handleValidate = useDebounce(async () => {
    setIsValidateLoading(true);
    const saveResult = await handleSaveAsDraft(state?.id ? [state.id] : [], invoiceNo, false);
    
    // Check if threshold warning was returned
    if (saveResult && saveResult.warning) {
      // Get move_ids from the original request (need to fetch from productData)
      const move_ids = fullProductData
        .filter(move => move.draft_produced_qty > 0)
        .map(move => ({
          move_id: move.id,
          producing_quantity: move.draft_produced_qty,
          damaged_quantity: move.draft_damaged_qty || 0
        }));
      
      // Show warning popup and wait for confirmation
      setThresholdWarningData(saveResult.data);
      setShowThresholdWarning(true);
      setPendingSaveCallback({
        picking_ids: state?.id ? [state.id] : [],
        container_number: invoiceNo,
        move_ids: move_ids,
        onComplete: async (saveSuccess) => {
    if (saveSuccess) {
            // Proceed with validate API after successful save
            validateBulkManufacturing({
              data: { picking_ids: state?.id },
            })
              .then((response) => {
                setIsValidateLoading(false);
                popup('success', response.message);
                window.history.back();
              })
              .catch((error) => {
                setIsValidateLoading(false);
                popup('error', error.message);
              });
          } else {
            setIsValidateLoading(false);
            popup('error', 'Failed to save draft quantities');
          }
        },
        onCancel: () => {
          // User cancelled - stop the process
          setIsValidateLoading(false);
        }
      });
      return; // Wait for user confirmation
    }
    
    // No warning - proceed normally
    if (saveResult === true) {
      validateBulkManufacturing({
        data: { picking_ids: state?.id },
      })
        .then((response) => {
          setIsValidateLoading(false);
          popup('success', response.message);
          window.history.back();
        })
        .catch(() => {
          setIsValidateLoading(false);
        });
    } else {
      setIsValidateLoading(false);
    }
  }, 400);

  const handleDetailsUpdate = () => {
    setIsUpdateDetailsLoading(true);
    const isInvoiceChanged =
      invoiceNo !== editValues.invoice_no && editValues.invoice_no !== '';

    if (!isInvoiceChanged) return;

    vendorUpdateDispatchDetail({
      data: {
        dispatch_id: [state?.id],
        invoice_no: isInvoiceChanged ? editValues?.invoice_no : false
      }
    }).then((response) => {
      if (response) {
        popup(POPUP_TYPE.SUCCESS, 'Invoice Number updated');
      }
      setIsUpdateDetailsLoading(false);
      getProductsList(true);
    }).catch(() => {
      setIsUpdateDetailsLoading(false);
    }).finally(() => {
      setIsUpdateDetailsLoading(false);
    })
  };

  // Fetch receipt header data (without move_ids)
  const getReceiptData = useDebounce(() => {
    setIsLoading(true);
    vendorReceipt({
      data: { receipt_ids: state?.id, page_number: 1 },
    })
      .then((response) => {
        setIsLoading(false);
        const records = response?.data?.records || [];
        if (records.length > 0) {
          setInvoiceNo(records[0]?.invoice_no);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, 400);

  // Fetch move_ids data with search and ready_status filter
  const getProductsList = useDebounce((additionalParams = {}) => {
    if (!state?.id) return;
    
    setIsMoveIdsLoading(true);
    
    const searchParams = {};
    
    // Add search term fields
    if (searchTerm) {
      searchParams.future_lot = searchTerm;
      searchParams['product_id.default_code'] = searchTerm;
      searchParams['product_id.name'] = searchTerm;
      searchParams['move_line_ids.lot_name'] = searchTerm;
    }
    
    // Add ready_status_filter to search if not 'All'
    if (readyStatusFilter !== 'All') {
      searchParams.ready_status = readyStatusFilter?.toString().toLowerCase().replaceAll(' ', '_');
    }
    
    const params = {
      receipt_id: state.id,
      page_number: additionalParams.page_number || pageNumber,
      search: searchParams,
      ...additionalParams,
    };

    vendorReceiptMoveIds({ data: params })
      .then((response) => {
        setIsMoveIdsLoading(false);
        if (response?.data?.records?.length > 0) {
          const compiledData = fabricManufacturingProductCompiler({ move_ids: response.data.records });
          setFullProductData(compiledData);
          setPaginationData(response.data.params);
          
          // Get ready_status options from API response
          if (response?.data?.ready_status) {
            setReadyStatusOptions(['All', ...response.data.ready_status.map(s => s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' '))]);
          }
        } else {
          setFullProductData([]);
          setPaginationData({ page: 1, start: 0, end: 0, total_records: 0 });
        }
      })
      .catch(() => {
        setIsMoveIdsLoading(false);
      });
  }, 400);

  // Handle search - trigger API call
  useEffect(() => {
    if (search && state?.id) {
      setPageNumber(1);
      getProductsList({ page_number: 1 });
    }
  }, [search, state?.id, getProductsList]);

  // Handle ready status filter change
  useEffect(() => {
    if (state?.id) {
      setPageNumber(1);
      getProductsList({ page_number: 1 });
    }
  }, [readyStatusFilter, state?.id, getProductsList]);

  // Handle page change
  useEffect(() => {
    if (state?.id && pageNumber > 0) {
      getProductsList({ page_number: pageNumber });
    }
  }, [pageNumber, state?.id, getProductsList]);

  useEffect(() => {
    if (state?.id) {
      getReceiptData();
      getProductsList();
    }
  }, [reload, state?.id, getReceiptData, getProductsList]);

  useEffect(() => {
    if (!state) {
      navigate(ROUTES.MANUFACTURING);
      return;
    }

    const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
    const status = state?.state;

    setColumn(() => {
      let updated = [...FABRIC_PRODUCTION_DETAILS_COLUMN];

      // Always remove 'Clear'
      updated = updated.filter((col) => col.name !== 'Clear');

      // Remove 'Action' column if production is done
      const isDone = productData?.[0]?.state === 'done' || state?.state === 'done';
      if (isDone) {
        updated = updated.filter((col) => col.name !== 'Action');
      }

      if (vendorType !== 'fabric') {
        // Remove 'Rolls', 'Color', 'Size' if not fabric
        updated = updated.filter((col) => !['Rolls', 'Color', 'Size'].includes(col.name));
      }

      if (vendorType !== 'dyeing') {
        // Remove 'Ready Status' if not dyeing
        updated = updated.filter((col) => col.name !== 'Ready Status');
      }

      if (vendorType === 'fabric' && status !== 'done') {
        // Keep only 'Produced Qty' and 'Damaged Qty', remove 'Produced Quantity', 'Damaged Quantity'
        updated = updated.filter((col) =>
          !['Action', 'Produced Quantity', 'Damaged Quantity'].includes(col.name)
        );
      }

      if (vendorType === 'fabric' && status === 'done') {
        // Keep only 'Produced Qty' and 'Damaged Qty', remove 'Produced Quantity', 'Damaged Quantity'
        updated = updated.filter((col) =>
          !['Action', 'Produced Qty', 'Damaged Qty'].includes(col.name)
        );
      }

      if (vendorType === 'dyeing' && status !== 'done') {
        // Keep only 'Produced Qty' and 'Damaged Qty', remove 'Produced Quantity', 'Damaged Quantity'
        updated = updated.filter((col) =>
          !['Produced Quantity', 'Damaged Quantity'].includes(col.name)
        );
      }

      if (vendorType === 'dyeing' && status === 'done') {
        // Keep only 'Produced Qty' and 'Damaged Qty', remove 'Produced Quantity', 'Damaged Quantity'
        updated = updated.filter((col) =>
          !['Produced Qty', 'Damaged Qty'].includes(col.name)
        );
      }

      return updated;
    });
  }, [productData, state, navigate]);

  const isUpdateDetailsDisabled = () => {
    const isInvoiceUnchanged =
      invoiceNo === editValues.invoice_no || !editValues.invoice_no;

    return (isInvoiceUnchanged) || isUpdateDetailsLoading;
  };

  return (
    <>
      <TitleBar
        title={[
          { text: TITLES.MANUFACTURING, route: ROUTES.MANUFACTURING },
          { text: state?.name, route: ROUTES.NULL },
        ]}
      />
      <div className="scroll-div">
        {(state?.state === 'draft' || state?.state === 'assigned') && (getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) !== 'fabric') && (
          <div className="d-flex justify-content-between gap-2 mb-3">
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${isUpdateDetailsDisabled() ? 'disabled' : ''
                }`}
              clickHandler={handleDetailsUpdate}
              disabled={isUpdateDetailsDisabled()}
            >
              {isUpdateDetailsLoading && (
                <span
                  className="spinner-border spinner-border-sm text-white me-2"
                  role="status"
                />
              )}
              {isUpdateDetailsLoading ? 'Updating' : 'Update Details'}
            </Button>
            <Button
              disabled={!enableValidate() || isValidateLoading}
              clickHandler={handleValidate}
              className={`border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3 ${enableValidate() && !isValidateLoading ? '' : 'disabled'
                }`}
            >
              {isValidateLoading && (
                <span
                  className="spinner-border spinner-border-sm text-white me-2"
                  role="status"
                />
              )}
              {BUTTON.VALIDATE}
            </Button>
          </div>
        )}
        <DetailBox colCount={2} invoiceNo={invoiceNo} colData={state} dataCompiler={dispatchDetailsBox} dispatchData={getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' ? true : false} value={editValues} setValue={setEditValues} isLoading={isLoading} />
        
        {/* Search and Ready Status Filter Section - Only for dyeing vendors */}
        {getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE) === 'dyeing' && (
          <div className="d-flex align-items-center justify-content-start gap-3 w-100 mb-3 mt-3">
            <SearchBar
              search={search}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
              setSearch={setSearch}
              onDefaultSearch={() => {
                setPageNumber(1);
                getProductsList({ page_number: 1 });
              }}
            />
            <div className="w-50 mb-3 filter-btns-div d-flex align-items-center justify-content-start">
              <DropDown 
                dropdownMenu={readyStatusOptions} 
                value={readyStatusFilter} 
                setValue={setReadyStatusFilter} 
              />
            </div>
          </div>
        )}
        
        <PaginatedTable
          columns={column}
          initialData={productData}
          colFilter={false}
          popUp={isModalOpen}
          setPopUp={setIsModalOpen}
          setPopUpData={setPopUpData}
          paginationData={paginationData}
          setPageNumber={setPageNumber}
          isLoading={isMoveIdsLoading || isLoading}
          showCollapseTable={showCollapseTable}
          collapseTableDataFunction={vendorModalDispatch}
        />
      </div>
      {isModalOpen && (
        <PopUpBox openPopUpBox={isModalOpen}>
          <FabricProductionModal
            setProductData={setProductData}
            closePopUp={setIsModalOpen}
            modalData={popUpData}
            setReload={setReload}
          />
        </PopUpBox>
      )}
      {showThresholdWarning && thresholdWarningData && (
        <PopUpBox openPopUpBox={showThresholdWarning} modalWidth="50%">
          <ThresholdWarningPopup
            warningData={thresholdWarningData}
            onConfirm={handleThresholdWarningConfirm}
            onCancel={handleThresholdWarningCancel}
            allowEdit={true}
          />
        </PopUpBox>
      )}
    </>
  );
};

